import React, {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from "react";
import searchIcon from "../../../assets/images/search-icon.svg";
import { Input, InputGroup, InputGroupAddon, InputGroupText } from "reactstrap";
import {
  ICategoryCityLevelListProps,
  ICategoryListingNormalizedData,
  IIdValuePairResponse,
} from "../../../interfaces/Index";
import { _ } from "../../../utils/lib";
import "./CategoryCityLevelList.scss";
import {
  bodyDataAndKeyTypes,
  footerData,
  headerData,
} from "./CategoryCityLevelConstants";
import LoaderComponent from "../../common/Loader/Loader";
import DataTableWithSubTable from "../../common/DataTableWithSubTable/DataTableWithSubTable";
import { Can } from "../../../config/Can";
import SearchableSelect from "../../common/SearchableSelect/SearchableSelect";
import Toaster from "../../common/Toaster/Toaster";

const CategoryCityLevelList: FunctionComponent<ICategoryCityLevelListProps> = (
  props: ICategoryCityLevelListProps
) => {
  const [selectedCity, setSelectedCity] = useState<IIdValuePairResponse | null>(
    props.retainedSelectedCity
  );
  const [searchText, setSearchText] = useState<string>(
    props.retainedSearchText
  );
  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  useEffect(() => {
    props.fetchCities();
  }, []);

  useEffect(() => {
    if (!selectedCity) {
      const newSelectedCity =
        props.citiesList && props.citiesList.length
          ? props.citiesList[0]
          : null;
      handleSelectedCityChange(newSelectedCity);
    }
  }, [props.citiesList]);

  useEffect(() => {
    const { isError, error } = props;
    if (isError && error) {
      alert((error as any).errors);
    }
  }, [props.isError]);
  useEffect(() => {
    if (props.isCategoryUpdated) {
      const message = "Category updated successfully";
      handleToaster(false, message);
    }
  }, [props.isCategoryUpdated]);

  const debouncedRefreshList = useRef(
    _.debounce(
      (selectedCity: IIdValuePairResponse, searchText: string) =>
        refreshList(selectedCity, searchText),
      1000
    )
  ).current;
  const refreshList = (
    selectedCity: IIdValuePairResponse,
    searchText: string
  ) => {
    props.fetchCategories(
      selectedCity.id,
      searchText ? { searchKey: searchText } : {}
    );
  };

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };
  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    setSearchText(searchText);
    if (selectedCity && (!searchText || searchText.length > 3)) {
      debouncedRefreshList(selectedCity, searchText);
    }
  };
  const handleSelectedCityChange = (
    selectedCity: IIdValuePairResponse | null
  ) => {
    setSelectedCity(selectedCity);
    if (selectedCity) {
      refreshList(selectedCity, searchText);
    }
  };
  const handleCategoryRowClick = (
    e: any,
    outerIndex?: number,
    innerIndex?: number
  ) => {
    const { updatePageHandler, categoryList } = props;
    const clickedCategory: ICategoryListingNormalizedData =
      categoryList[outerIndex as number][innerIndex as number];
    const clickedCategoryParent = categoryList[outerIndex as number].find(
      (cat) => cat.id === clickedCategory.parentId
    );
    updatePageHandler(
      searchText,
      window.pageYOffset,
      selectedCity || null,
      clickedCategory,
      clickedCategoryParent
    );
  };

  return (
    <div className="data-table-container category-city-list-container">
      {props.isLoading && <LoaderComponent />}
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-11">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src={searchIcon} alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by name or handle"
                onChange={handleSearchInputChange}
                value={searchText}
              />
              <div className="input-group-append">
                <SearchableSelect
                  id="selectedCity"
                  isSearchable={true}
                  options={props.citiesList}
                  selectedValue={selectedCity}
                  onChange={handleSelectedCityChange}
                />
              </div>
            </InputGroup>
          </section>
        </section>
      </section>
      <Can I="read" a="category">
        <DataTableWithSubTable
          tableId="category-list"
          tableHeaderData={headerData}
          tableFooterData={footerData}
          tableBodyDataTypes={bodyDataAndKeyTypes}
          tableBodyRowData={props.categoryList}
          noSearchResultFlag={!!searchText}
          editRowHandler={handleCategoryRowClick}
        />
      </Can>
    </div>
  );
};

export default CategoryCityLevelList;
